/**
 * Index of the slice column nearest x=0 (the on-wellbore sample). The 2D
 * Profile chart reads each formation's depth at this column to plot one
 * Y value per eval point along the wellbore.
 * @param {number[] | undefined} slicePositions - X positions of each slice column.
 * @return {number} Zero when the list is empty or unset; otherwise the index
 *   with the smallest absolute value.
 */
export function centerColumnIndex(
  slicePositions: number[] | undefined
): number {
  if (!slicePositions?.length) return 0;
  let bestIdx = 0;
  let bestAbs = Math.abs(slicePositions[0]);
  for (let i = 1; i < slicePositions.length; i++) {
    const abs = Math.abs(slicePositions[i]);
    if (abs < bestAbs) {
      bestAbs = abs;
      bestIdx = i;
    }
  }
  return bestIdx;
}

export interface FocusedTrajectoryDepthsInput {
  /** [vs, tvdss] pairs — the wellbore's full profile2D trajectory. */
  trajectory: number[][];
  tvdValues: number[] | undefined;
  /** True for a vertical bore (well-level holeDirection === 'VERTICAL') —
   *  there's no lateral to isolate, so the full trajectory is used. */
  isVertical: boolean;
  heelVs?: number | null;
  lateralEndpoints?: {
    heel: { vs: number; tvdss: number };
    toe: { vs: number; tvdss: number };
  };
}

export interface FocusedTrajectoryDepths {
  tvdss: number[];
  tvd: number[];
}

/**
 * Collects the subset of a wellbore's trajectory used to focus the 2D
 * Profile's initial zoom. Lateral (horizontal/directional) bores isolate the
 * points near the lateral using, in priority order: BE-provided heel/toe
 * endpoints, the heelVs cutoff, or a near-max-depth fallback. A vertical bore
 * has no lateral to isolate, so the full surface→TD trajectory is used
 * instead — otherwise the near-max-depth fallback would clip the focused
 * zoom to only the bottom ~500 ft of a fully vertical wellbore.
 * @param {FocusedTrajectoryDepthsInput} input - Trajectory + orientation data.
 * @return {FocusedTrajectoryDepths} TVDSS/TVD values for the points selected
 *   to drive the focused initial zoom.
 */
export function collectFocusedTrajectoryDepths({
  trajectory,
  tvdValues,
  isVertical,
  heelVs,
  lateralEndpoints,
}: FocusedTrajectoryDepthsInput): FocusedTrajectoryDepths {
  const tvdss: number[] = [];
  const tvd: number[] = [];
  const pushPoint = (y: number, idx: number): void => {
    tvdss.push(y);
    if (tvdValues?.[idx] !== undefined) tvd.push(tvdValues[idx]);
  };

  if (isVertical) {
    trajectory.forEach(([, y], idx) => pushPoint(y, idx));
    return { tvdss, tvd };
  }

  if (lateralEndpoints) {
    // Use TVDSS range from backend to identify lateral trajectory points
    const lateralMinTvdss = Math.min(
      lateralEndpoints.heel?.tvdss,
      lateralEndpoints.toe?.tvdss
    );
    const lateralMaxTvdss = Math.max(
      lateralEndpoints.heel?.tvdss,
      lateralEndpoints.toe?.tvdss
    );
    const buffer = 200; // ft buffer around the lateral depth range
    trajectory.forEach(([, y], idx) => {
      if (y >= lateralMinTvdss - buffer && y <= lateralMaxTvdss + buffer) {
        pushPoint(y, idx);
      }
    });
    return { tvdss, tvd };
  }

  if (heelVs !== null && heelVs !== undefined && heelVs > 0) {
    trajectory.forEach(([x, y], idx) => {
      if (x >= heelVs) pushPoint(y, idx);
    });
    return { tvdss, tvd };
  }

  if (trajectory.length > 0) {
    // heelVs unavailable — approximate lateral as points near maximum depth
    const allTrajTvdss = trajectory.map(([, y]) => y);
    const deepestTvdss = Math.min(...allTrajTvdss);
    const tvdssThreshold = deepestTvdss + 500;
    trajectory.forEach(([, y], idx) => {
      if (y <= tvdssThreshold) pushPoint(y, idx);
    });
  }

  return { tvdss, tvd };
}
